// BOT-EDIT: 2025-01-27T10:35:00Z Add invoice management and enhanced card actions to DashboardPage
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { CardService, CardData } from '../services/cardService';
import { UserService, UserProfile } from '../services/userService';
import { QRService } from '../services/qrService';
import QRCode from '../components/QRCode';

import SecureInvoiceViewer from '../components/SecureInvoiceViewer';

const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { userPlan, isPro, isStandard, isCardOnly, isTrialActive, isExpired, trialEndsAt, planExpiresAt, isLoading: subscriptionLoading, refreshSubscription } = useSubscription();

  // 구독 정보 디버깅
  useEffect(() => {
    console.log('🔍 [DashboardPage] 구독 정보 디버그:', {
      userPlan,
      isPro,
      isStandard,
      isCardOnly,
      isTrialActive,
      isExpired,
      trialEndsAt: trialEndsAt?.toISOString(),
      planExpiresAt: planExpiresAt?.toISOString(),
      subscriptionLoading
    });
  }, [userPlan, isPro, isStandard, isCardOnly, isTrialActive, isExpired, trialEndsAt, planExpiresAt, subscriptionLoading]);

  // 🆕 결제 성공 시 구독 상태 새로고침 (대시보드에서도)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success') === 'true';
    const sessionId = urlParams.get('session_id');
    
    if (paymentSuccess || sessionId) {
      console.log('🔄 [DashboardPage] 결제 성공 감지, 구독 상태 새로고침 예약');
      
      // 5초 후 구독 상태 새로고침 (웹훅 처리 시간 충분히 고려)
      const refreshTimer = setTimeout(() => {
        console.log('🔄 [DashboardPage] 구독 상태 새로고침 실행');
        refreshSubscription();
      }, 5000);
      
      return () => clearTimeout(refreshTimer);
    }
  }, [refreshSubscription]);

  // i18n 디버그 정보
  useEffect(() => {
    console.log('🔍 Dashboard i18n Debug Info:');
    console.log('  - Current language:', i18n.language);
    console.log('  - Available languages:', i18n.languages);
    console.log('  - Is initialized:', i18n.isInitialized);
    console.log('  - Fallback language:', i18n.options.fallbackLng);
    console.log('  - Supported languages:', i18n.options.supportedLngs);
    
    // 번역 테스트
    console.log('🔍 Translation Test:');
    console.log('  - nav.tab.home:', t('nav.tab.home'));
    console.log('  - common.email:', t('common.email'));
    console.log('  - common.login:', t('common.login'));
  }, [i18n.language, i18n.isInitialized, t]);

  const [qrOpen, setQrOpen] = useState(false);
  const [qrValue, setQrValue] = useState('');
  const [qrDesign, setQrDesign] = useState<any>(null);
  const [selectedCard, setSelectedCard] = useState<CardData | null>(null);

  // 더보기 메뉴 상태
  const [showMoreActions, setShowMoreActions] = useState<string | null>(null);

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userCards, setUserCards] = useState<CardData[]>([]);
  const [loading, setLoading] = useState(true);

  // 인보이스 관리 모달 상태
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedCardForInvoice, setSelectedCardForInvoice] = useState<CardData | null>(null);
  
  // 보안 인보이스 뷰어 상태
  const [showSecureViewer, setShowSecureViewer] = useState(false);
  const [viewerInvoiceId, setViewerInvoiceId] = useState<string>('');
  const [viewerCardId, setViewerCardId] = useState<string>('');

  // 안전한 번역 함수
  const safeT = (key: string, options: any = {}) => {
    try {
      const result = t(key, options);
      return typeof result === 'string' ? result : key;
    } catch (error) {
      console.warn('Translation error for key:', key, error);
      return key;
    }
  };

  // 날짜 포맷 함수
  const formatDate = (date: Date | null) => {
    if (!date) return safeT('common.noData', '정보 없음');
    return date.toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 남은 일수 계산 함수
  const getDaysRemaining = (date: Date | null) => {
    if (!date) return 0;
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // 플랜 이름 번역
  const getPlanDisplayName = (plan: string) => {
    switch (plan) {
      case 'free_trial':
        return safeT('plan.free_trial', '무료 체험');
      case 'card_only':
        return safeT('plan.card_only', '명함 전용');
      case 'standard':
        return safeT('plan.standard', '스탠다드');
      case 'pro':
        return safeT('plan.pro', '프로');
      case 'expired':
        return safeT('plan.expired', '만료됨');
      default:
        return plan;
    }
  };



  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user?.uid) {
        console.log('⚠️ No user UID available, waiting for auth...');
        return;
      }
      
      try {
        setLoading(true);
        console.log('🔄 Loading dashboard data for user:', user.uid);
        
        // 새로고침 대응: 잠시 대기
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // getOrCreateUserProfile 사용하여 프로필 보장
        const profile = await UserService.getOrCreateUserProfile(user.uid, {
          email: user.email || '',
          displayName: user.displayName || undefined,
          photoURL: user.photoURL || undefined
        });
        
        console.log('✅ Profile loaded/created:', profile);
        setUserProfile(profile);
        
        // 카드 데이터 로드
        const cards = await CardService.getUserCards(user.uid);
        console.log('✅ Cards loaded:', cards.length);
        setUserCards(cards);
        
      } catch (error) {
        console.error('❌ Dashboard data load failed:', error);
        // 에러 발생 시 기본 프로필 설정
        setUserProfile({
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || undefined,
          photoURL: user.photoURL || undefined,
          plan: 'free_trial',
          cardCount: 0
        });
      } finally {
        setLoading(false);
      }
    };

    // 인증 상태가 완전히 로드된 후에만 데이터 로드
    if (user?.uid) {
      console.log('�� User authenticated, loading dashboard data...');
      loadDashboardData();
    } else {
      console.log('⏳ Waiting for user authentication...');
      setLoading(true);
    }
  }, [user?.uid]);

  const handleLogout = async () => {
    try {
      await logout();
      // 로그아웃 후 홈페이지로 리다이렉트
      navigate('/');
    } catch (error) {
      console.error(t('common.error', 'Logout failed'), error);
    }
  };



  const handleQRView = async (card: CardData) => {
    try {
      setSelectedCard(card);
      
      // QR 코드 URL 설정 - following app logic
      let url = '';
      if (card.customPath) {
        url = `https://scanvcf.com/${card.customPath}`;
      } else if (card.customDomain) {
        url = `https://${card.customDomain}`;
      } else if (card.shareUrl) {
        url = card.shareUrl;
      } else {
        url = `https://scanvcf.com/card/${user!.uid}/${card.id}`;
      }
      setQrValue(url);
      
      // 기존 QR 디자인 데이터 마이그레이션
      await QRService.migrateQRDesignData(user!.uid, card.id!);
      
      // 저장된 QR 디자인 불러오기
      const savedQRDesign = await QRService.getQRDesign(user!.uid, card.id!);
      setQrDesign(savedQRDesign);
      
      setQrOpen(true);
    } catch (error) {
      console.error('Failed to load QR design:', error);
      // 기본 QR 코드로 표시 - following app logic
      let url = '';
      if (card.customPath) {
        url = `https://scanvcf.com/${card.customPath}`;
      } else if (card.customDomain) {
        url = `https://${card.customDomain}`;
      } else if (card.shareUrl) {
        url = card.shareUrl;
      } else {
        url = `https://scanvcf.com/card/${user!.uid}/${card.id}`;
      }
      setQrValue(url);
      setQrDesign(null);
      setQrOpen(true);
    }
  };

  // 인보이스 관리 모달 열기
  const handleInvoiceManagement = (card: CardData) => {
    // ✅ 모든 플랜에서 인보이스 관리 가능 (추후 제한 필요시 구독 컨텍스트에서 처리)
    setSelectedCardForInvoice(card);
    setShowInvoiceModal(true);
  };

  // 인보이스 관리 옵션 선택
  const handleInvoiceOption = (option: string, card: CardData) => {
    setShowInvoiceModal(false);
    setSelectedCardForInvoice(null);
    
    switch (option) {
      case 'create':
        navigate('/invoice/create', { 
          state: { cardData: card, cardLanguage: 'ko' }
        });
        break;
      case 'list':
        navigate('/invoices', { 
          state: { cardId: card.id, cardData: card, cardLanguage: 'ko' }
        });
        break;
      case 'settings':
        navigate('/business-profile', { 
          state: { cardId: card.id, cardLanguage: 'ko' }
        });
        break;
    }
  };

  // 보안 인보이스 뷰어 열기 (예시 - 실제로는 인보이스 ID가 필요)
  const handleSecureViewInvoice = (cardId: string, invoiceId: string) => {
    setViewerCardId(cardId);
    setViewerInvoiceId(invoiceId);
    setShowSecureViewer(true);
  };

  // 카드 공유
  const handleShareCard = async (card: CardData) => {
    try {
      let shareUrl = '';
      if (card.customPath) {
        shareUrl = `https://scanvcf.com/${card.customPath}`;
      } else if (card.customDomain) {
        shareUrl = `https://${card.customDomain}`;
      } else if (card.shareUrl) {
        shareUrl = card.shareUrl;
      } else {
        shareUrl = `https://scanvcf.com/card/${user!.uid}/${card.id}`;
      }
      
      // 클립보드에 URL 복사
      await navigator.clipboard.writeText(shareUrl);
      
      // 성공 메시지 표시
      alert('링크가 복사되었습니다');
      
    } catch (error) {
      console.error('Error sharing card:', error);
      alert('공유 중 오류가 발생했습니다');
    }
  };

  // 카드 링크 복사
  const handleCopyLink = async (card: CardData) => {
    try {
      let shareUrl = '';
      if (card.customPath) {
        shareUrl = `https://scanvcf.com/${card.customPath}`;
      } else if (card.customDomain) {
        shareUrl = `https://${card.customDomain}`;
      } else if (card.shareUrl) {
        shareUrl = card.shareUrl;
      } else {
        shareUrl = `https://scanvcf.com/card/${user!.uid}/${card.id}`;
      }
      
      await navigator.clipboard.writeText(shareUrl);
      
      // URL 복사 이벤트를 Cloud Function에 전송
      const logEventUrl = 'https://us-central1-scanvcf-375fb.cloudfunctions.net/logEvent';
      const response = await fetch(logEventUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cardId: card.id,
          eventType: 'urlCopy',
          userId: user!.uid,
          timestamp: new Date().toISOString()
        })
      });

      console.log('◆◆ [DashboardPage] URL 복사 이벤트 응답:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      alert(safeT('functions.share', '링크가 복사되었습니다'));
    } catch (error) {
      console.error('Error copying link:', error);
      alert(safeT('common.error', '링크 복사 중 오류가 발생했습니다'));
    }
  };

  // 카드 삭제
  const handleDeleteCard = async (cardId: string, cardName: string) => {
    const confirmDelete = window.confirm(
      `${safeT('common.delete', '카드 삭제')}\n${safeT('common.delete', `정말로 ${cardName} 카드를 삭제하시겠습니까?`)}`
    );
    
    if (confirmDelete) {
      try {
        await CardService.deleteCard(user!.uid, cardId);
        // 카드 목록 새로고침
        const cards = await CardService.getUserCards(user!.uid);
        setUserCards(cards);
        alert(safeT('common.delete', '카드가 삭제되었습니다'));
      } catch (error) {
        console.error('Error deleting card:', error);
        alert(safeT('common.error', '카드 삭제 중 오류가 발생했습니다'));
      }
    }
  };

  // 카드 공개 프로필 보기
  const handleViewProfile = (card: CardData) => {
    navigate(`/cards/${card.id}/view`, {
      state: { cardData: card, userId: user!.uid }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {!user ? t('common.loading', 'Checking authentication status...') : t('common.loading', 'Loading data...')}
          </p>
          <p className="mt-2 text-sm text-gray-500">
            {!user ? t('common.loading', 'Please refresh and wait a moment') : ''}
          </p>
        </div>
      </div>
    );
  }

  // 사용자가 없으면 로그인 페이지로 리다이렉트
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{t('common.loading', 'Login required.')}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            {t('common.login', 'Login')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100">
      {/* 페이지 헤더 */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">{t('nav.tab.home', 'Dashboard')}</h1>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700">
                {t('common.businessCard', 'Hello')}, {user?.displayName || user?.email}!
              </span>
              <button
                onClick={handleLogout}
                className="bg-slate-600 hover:bg-slate-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center"
              >
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {t('auth.logout', '로그아웃')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* 사용자 정보 및 구독 상태 카드 */}
          <div className="bg-white overflow-hidden shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {t('common.user', '사용자')} {t('common.info', '정보')}
                </h3>
                {/* 구독 상태 배지 */}
                <div className="flex flex-col items-end space-y-2">
                  {!isTrialActive && (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      isExpired 
                        ? 'bg-red-100 text-red-800' 
                        : isPro 
                          ? 'bg-purple-100 text-purple-800'
                          : isStandard
                            ? 'bg-blue-100 text-blue-800'
                            : isCardOnly
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {isExpired ? '⏰ 만료됨' : getPlanDisplayName(userPlan)}
                    </span>
                  )}
                  {isTrialActive && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                      🎯 {t('subscription.plans.freeTrialName', '체험 중')}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* 기본 정보 */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700 border-b pb-1">{t('settings.profileInfo', '기본 정보')}</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-600">{t('common.email', '이메일')}:</span> <span className="font-medium">{user?.email}</span></p>
                    <p><span className="text-gray-600">{t('common.fields.name', '이름')}:</span> <span className="font-medium">{userProfile?.displayName || safeT('common.noName', '설정 안됨')}</span></p>
                    <p><span className="text-gray-600">{t('common.businessCard', 'Cards')}:</span> <span className="font-medium">{userCards.length}</span></p>
                  </div>
                </div>

                {/* 구독 정보 */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700 border-b pb-1">{t('subscription.currentStatus', 'Subscription Info')}</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-600">{t('subscription.currentPlan', 'Current Plan: {{plan}}', { plan: getPlanDisplayName(userPlan) })}</span></p>
                    <p><span className="text-gray-600">{t('invoiceEdit.status', 'Status *')}:</span> 
                      <span className={`font-medium ml-1 ${isExpired ? 'text-red-600' : 'text-green-600'}`}>
                        {isExpired ? t('subscription.expired.title', 'Expired') : t('common.Active', 'Active')}
                      </span>
                    </p>
                    {isTrialActive && (
                      <p><span className="text-gray-600">{t('subscription.trialStatus', 'Trial Status')}:</span> <span className="font-medium text-orange-600">{t('subscription.plans.freeTrialName', 'Active')}</span></p>
                    )}
                  </div>
                </div>

                {/* 만료일 정보 */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700 border-b pb-1">{t('subscription.currentStatus', 'Expiration Info')}</h4>
                  <div className="space-y-2 text-sm">
                    {subscriptionLoading ? (
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="animate-pulse">
                          <div className="h-3 bg-gray-300 rounded mb-2"></div>
                          <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* 무료 체험 만료일 */}
                        {userPlan === 'free_trial' && trialEndsAt && (
                          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                            <p className="text-orange-800 font-medium">
                              🎯 {t('subscription.expiringIn', 'Expires in {{days}} days', { days: getDaysRemaining(trialEndsAt) })}
                            </p>
                            <p className="text-orange-700 text-xs mt-1">{formatDate(trialEndsAt)}</p>
                          </div>
                        )}
                        
                        {/* 유료 플랜 만료일 */}
                        {(userPlan === 'card_only' || userPlan === 'standard' || userPlan === 'pro') && planExpiresAt && (
                          <div className={`p-3 border rounded-lg ${
                            isExpired 
                              ? 'bg-red-50 border-red-200' 
                              : 'bg-blue-50 border-blue-200'
                          }`}>
                            <p className={`font-medium ${isExpired ? 'text-red-800' : 'text-blue-800'}`}>
                              {isExpired 
                                ? `⏰ ${t('subscription.expired.title', 'Subscription Expired')}` 
                                : `📅 ${t('subscription.expiringIn', 'Expires in {{days}} days', { days: getDaysRemaining(planExpiresAt) })}`
                              }
                            </p>
                            <p className={`text-xs mt-1 ${isExpired ? 'text-red-700' : 'text-blue-700'}`}>
                              {formatDate(planExpiresAt)}
                            </p>
                          </div>
                        )}
                        
                        {/* 만료일 정보가 없는 경우 */}
                        {!trialEndsAt && !planExpiresAt && (
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="text-gray-600 text-xs">{t('subscription.noExpiration', 'No expiration info')}</p>
                            <p className="text-gray-500 text-xs mt-1">
                              {t('subscription.currentStatus', 'Plan')}: {getPlanDisplayName(userPlan)}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* 만료 경고 또는 업그레이드 버튼 */}
              {isExpired && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-red-800">{t('subscription.expired.title', 'Subscription Expired')}</h4>
                    <p className="text-xs text-red-600 mt-1">{t('subscription.expired.title', 'Please renew your subscription to continue using the service.')}</p>
                  </div>
                  <button
                    onClick={() => navigate('/paywall')}
                    className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                  >
                    {t('subscription.upgrade', 'Renew Subscription')}
                  </button>
                </div>
              )}
              
              {userPlan === 'free_trial' && !isExpired && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-blue-800">{t('subscription.plans.freeTrialName', 'Free Trial Active')}</h4>
                    <p className="text-xs text-blue-600 mt-1">{t('subscription.upgrade', 'Upgrade your plan to access more features.')}</p>
                  </div>
                  <button
                    onClick={() => navigate('/paywall')}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {t('subscription.upgrade', 'Upgrade')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 카드 목록 */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {t('navigation.myCards', '내 명함')} ({userCards.length})
                </h3>
                <Link
                  to="/card/create"
                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  + {t('nav.tab.addCard', '명함 추가')}
                </Link>
              </div>
              
              {userCards.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {t('common.noData', 'No cards created yet.')}
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {userCards.map((card) => (
                    <div key={card.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200 hover:border-gray-300">
                      {/* 카드 헤더 */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                            {card.photoUrl ? (
                              <img src={card.photoUrl} alt="Profile" className="w-12 h-12 rounded-full object-cover" />
                            ) : (
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{card.name}</h4>
                            <p className="text-sm text-gray-600">{card.position}</p>
                          </div>
                        </div>
                        
                        {/* 퀵 액션 버튼 */}
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleQRView(card)}
                            className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200"
                            title={safeT('functions.share', 'QR 코드 보기')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleCopyLink(card)}
                            className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all duration-200"
                            title={safeT('functions.share', '링크 복사')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleShareCard(card)}
                            className="p-2 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all duration-200"
                            title={safeT('functions.share', '공유')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => navigate(`/stats/${card.id}`)}
                            className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200"
                            title={safeT('functions.share', '통계 보기')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 mb-1">{card.email}</p>
                      <p className="text-sm text-gray-600 mb-1">{card.company}</p>

                      {/* URL Display - following app logic */}
                      {(() => {
                        let displayUrl = '';
                        if (card.customPath) {
                          displayUrl = `https://scanvcf.com/${card.customPath}`;
                        } else if (card.customDomain) {
                          displayUrl = `https://${card.customDomain}`;
                        } else if (card.shareUrl) {
                          displayUrl = card.shareUrl;
                        } else {
                          displayUrl = 'no-url';
                        }
                        
                        return displayUrl !== 'no-url' ? (
                          <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm text-blue-700 font-medium">{t('common.website', 'Website')}:</span>
                              </div>
                              <button
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(displayUrl);
                                    
                                    // URL 복사 이벤트를 Cloud Function에 전송
                                    const logEventUrl = 'https://us-central1-scanvcf-375fb.cloudfunctions.net/logEvent';
                                    const response = await fetch(logEventUrl, {
                                      method: 'POST',
                                      headers: {
                                        'Content-Type': 'application/json',
                                      },
                                      body: JSON.stringify({
                                        cardId: card.id,
                                        eventType: 'urlCopy',
                                        userId: user!.uid,
                                        timestamp: new Date().toISOString()
                                      })
                                    });

                                    console.log('◆◆ [DashboardPage] URL 복사 이벤트 응답:', {
                                      status: response.status,
                                      statusText: response.statusText,
                                      ok: response.ok
                                    });

                                    alert(t('qr.copied', '링크가 복사되었습니다'));
                                  } catch (error) {
                                    console.error('Error copying URL:', error);
                                    alert(t('functions.share', '링크가 복사되었습니다'));
                                  }
                                }}
                                className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-md transition-all duration-200"
                                title={t('functions.share', '링크 복사')}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </button>
                            </div>
                            <a
                              href={displayUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-sm text-blue-600 hover:text-blue-800 mt-2 break-all transition-colors cursor-pointer font-medium"
                            >
                              {displayUrl}
                            </a>
                          </div>
                        ) : null;
                      })()}

                      {/* 메인 액션 버튼 */}
                      <div className="mt-3 flex space-x-2">
                        <button
                          onClick={() => handleInvoiceManagement(card)}
                          className="flex-1 flex items-center justify-center px-3 py-2.5 bg-slate-800 text-white text-sm rounded-lg hover:bg-slate-700 transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {t('invoice.title', 'invoice')}
                        </button>
                        
                        <button
                          onClick={() => navigate(`/cards/${card.id}/edit`)}
                          className="flex-1 flex items-center justify-center px-3 py-2.5 bg-slate-600 text-white text-sm rounded-lg hover:bg-slate-500 transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          {safeT('common.edit', '편집')}
                        </button>
                      </div>

                      {/* 더보기 버튼 */}
                      <div className="mt-2">
                        <button
                          onClick={() => {
                            console.log('More button clicked for card:', card.id);
                            setShowMoreActions(prev => prev === card.id ? null : card.id || null);
                          }}
                          className="w-full flex items-center justify-center px-3 py-2.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          {showMoreActions === card.id ? t('common.close', '닫기') : t('moreActions', '더보기')}
                        </button>
                      </div>

                      {/* 더보기 메뉴 */}
                      {showMoreActions === card.id && (
                        <div className="mt-2 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => handleShareCard(card)}
                              className="flex items-center justify-center px-3 py-2.5 bg-slate-700 text-white text-sm rounded-lg hover:bg-slate-600 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                              </svg>
                              <span className="whitespace-nowrap">{t('common.share', '공유')}</span>
                            </button>
                            
                            <button
                              onClick={() => navigate(`/cards/${card.id}/qr-design`)}
                              className="flex items-center justify-center px-3 py-2.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-500 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                              </svg>
                              {t('cardEdit.qrDesign', 'QR Design')}
                            </button>
                            
                            <button
                              onClick={() => navigate(`/stats/${card.id}`)}
                              className="flex items-center justify-center px-3 py-2.5 bg-slate-600 text-white text-sm rounded-lg hover:bg-slate-500 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              {t('stats.title', '통계')}
                            </button>
                            
                            <button
                              onClick={() => handleDeleteCard(card.id!, card.name)}
                              className="flex items-center justify-center px-3 py-2.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-500 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              {safeT('common.delete', '삭제')}
                            </button>
                          </div>
                          
                          {/* 도메인 설정 버튼 - 새로운 행 */}
                          <div className="mt-2">
                            <button
                              onClick={() => navigate(`/domain-settings?cardId=${card.id}&initialPath=${card.customPath || ''}&initialDomain=${card.customDomain || ''}&isCustomDomain=false`)}
                              className="w-full flex items-center justify-center px-3 py-2.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-500 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                              </svg>
                              {t('domainSettings.title', '도메인')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* QR 모달 */}
        {qrOpen && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-xl p-6 w-[320px] text-center">
              <h4 className="font-semibold mb-3">{t('functions.share', 'QR 코드')}</h4>

              {/* QR 코드 - 저장된 디자인 적용 */}
              <div className="flex justify-center mb-3">
                <QRCode
                  value={qrValue}
                  size={220}
                  color={qrDesign?.qrColor || '#000000'}
                  backgroundColor={qrDesign?.qrBackgroundColor || '#FFFFFF'}
                  logoUrl={qrDesign?.qrLogoUrl}
                  logoSize={60}
                  logoBackgroundColor="white"
                  logoMargin={6}
                  logoBorderRadius={6}
                  ecl="H"
                />
              </div>

              <p className="mt-3 text-xs break-all text-gray-600">{qrValue}</p>
              <div className="mt-4 flex gap-2 justify-center">
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(qrValue);
                      
                      // URL 복사 이벤트를 Cloud Function에 전송
                      const logEventUrl = 'https://us-central1-scanvcf-375fb.cloudfunctions.net/logEvent';
                      const response = await fetch(logEventUrl, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          cardId: selectedCard?.id,
                          eventType: 'urlCopy',
                          userId: user!.uid,
                          timestamp: new Date().toISOString()
                        })
                      });

                      console.log('◆◆ [DashboardPage] QR URL 복사 이벤트 응답:', {
                        status: response.status,
                        statusText: response.statusText,
                        ok: response.ok
                      });

                      alert(t('functions.share', '링크가 복사되었습니다'));
                    } catch (error) {
                      console.error('Error copying QR URL:', error);
                      alert(t('functions.share', '링크가 복사되었습니다'));
                    }
                  }}
                  className="px-3 py-1 border rounded hover:bg-gray-50"
                >
                  {t('functions.share', '링크 복사')}
                </button>
                <button
                  onClick={() => setQrOpen(false)}
                  className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {t('common.close', '닫기')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 보안 인보이스 뷰어 */}
        {showSecureViewer && (
          <SecureInvoiceViewer
            invoiceId={viewerInvoiceId}
            cardId={viewerCardId}
            onClose={() => setShowSecureViewer(false)}
          />
        )}

        {/* 인보이스 관리 모달 */}
        {showInvoiceModal && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-xl p-6 w-[400px] max-w-[90vw]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {safeT('common.invoice', '인보이스')}
                </h3>
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={() => handleInvoiceOption('create', selectedCardForInvoice!)}
                  className="w-full flex items-center p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">{safeT('invoice.createNew', '새 인보이스 생성')}</div>
                    <div className="text-sm text-gray-600">{safeT('invoice.createNewDesc', '새로운 인보이스를 작성합니다')}</div>
                  </div>
                </button>

                <button
                  onClick={() => handleInvoiceOption('list', selectedCardForInvoice!)}
                  className="w-full flex items-center p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                >
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">{safeT('invoice.list.title', '인보이스 목록')}</div>
                    <div className="text-sm text-gray-600">{safeT('invoice.list.desc', '기존 인보이스들을 확인합니다')}</div>
                  </div>
                </button>

                <button
                  onClick={() => handleInvoiceOption('settings', selectedCardForInvoice!)}
                  className="w-full flex items-center p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                >
                  <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">{safeT('invoice.settings.title', '비즈니스 설정')}</div>
                    <div className="text-sm text-gray-600">{safeT('invoice.settings.settingsDesc', '비즈니스 프로필을 관리합니다')}</div>
                  </div>
                </button>
              </div>

              <button
                onClick={() => setShowInvoiceModal(false)}
                className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {safeT('common.close', '닫기')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;